import React from 'react';

/**
 * Global React error boundary. Catches render-time errors anywhere in the tree
 * and shows a clean fallback instead of the raw dev overlay. Errors still log
 * to console for debugging.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, errorInfo);

    // Suppress noise: ChunkLoadErrors are auto-recovered by the global handler
    // in index.js (page is force-reloaded with a cache-buster). No point storing
    // them in the alerts dashboard.
    const msg = String(error?.message || error || '').toLowerCase();
    const isChunkLoad = (
      msg.includes('loading chunk') ||
      msg.includes('loading css chunk') ||
      msg.includes('failed to fetch dynamically imported module') ||
      error?.name === 'ChunkLoadError'
    );
    if (isChunkLoad) return;

    // Fire-and-forget report to backend so admins see these in the dashboard
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      if (apiBase) {
        fetch(`${apiBase}/api/client-errors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: String(error?.message || error || 'Unknown error'),
            stack: String(error?.stack || ''),
            component_stack: String(errorInfo?.componentStack || ''),
            url: typeof window !== 'undefined' ? window.location.href : '',
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          }),
          keepalive: true,
        }).catch(() => {});
      }
    } catch (_) { /* never let reporting break the fallback */ }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center p-4 sm:p-6" data-testid="error-boundary">
        <div className="max-w-md w-full bg-[#1a1f2e] border border-[#C9A961]/30 p-6 sm:p-10 text-center">
          <div className="inline-block mb-4 px-3 py-1 border border-[#C9A961]/40 text-[#C9A961] text-[10px] tracking-[0.3em] uppercase">
            Unexpected Error
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-3">Something went wrong</h1>
          <p className="text-sm text-[#E8E4DC]/60 mb-6 leading-relaxed">
            We've hit a hiccup loading this page. Please refresh to continue — if the problem persists, head back to the homepage.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={this.handleReload}
              className="bg-[#C9A961] text-[#0F1419] px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#D4B76E] transition-all"
              data-testid="error-boundary-reload"
            >
              Refresh Page
            </button>
            <button
              onClick={this.handleHome}
              className="border border-[#C9A961]/40 text-[#C9A961] px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#C9A961]/10 transition-all"
              data-testid="error-boundary-home"
            >
              Go Home
            </button>
          </div>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre className="mt-6 text-left text-[11px] text-red-300/70 bg-black/40 p-3 rounded max-h-40 overflow-auto">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
